import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { leadsAPI } from "../../lib/api";
import { ArrowLeft, Mail, MessageCircle, Tag, Clock, Edit, Save, X, Plus } from "lucide-react";
import { toast } from "sonner";

const SEGMENTO_OPTIONS = [
    { value: "mulheres_com_proposito", label: "Mulheres com Propósito" },
    { value: "mesa_de_ester",          label: "Mesa de Ester" },
    { value: "luto_ao_recomeco",       label: "Do Luto ao Recomeço" },
    { value: "devocional",             label: "Devocional" },
    { value: "musica",                 label: "Música" },
    { value: "livro",                  label: "Livro" },
    { value: "geral",                  label: "Geral" },
];

const STATUS_OPTIONS = ["novo","engajado","convertido","inativo"];

const HISTORY_TYPES = [
    { value: "nota",             label: "Nota" },
    { value: "mensagem_enviada", label: "Mensagem enviada" },
    { value: "email_enviado",    label: "Email enviado" },
    { value: "compra",           label: "Compra" },
    { value: "evento",           label: "Evento" },
];

const SEGMENTO_COLORS = {
    mulheres_com_proposito: "bg-amber-800/40 text-amber-300",
    mesa_de_ester:          "bg-rose-800/40 text-rose-300",
    luto_ao_recomeco:       "bg-indigo-800/40 text-indigo-300",
    devocional:             "bg-sky-800/40 text-sky-300",
    musica:                 "bg-violet-800/40 text-violet-300",
    livro:                  "bg-emerald-800/40 text-emerald-300",
    geral:                  "bg-stone-700/40 text-stone-300",
};

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Avatar({ name = "", size = 56 }) {
    const initials = name.split(" ").slice(0,2).map(w => w[0]).join("").toUpperCase() || "?";
    const colors = ["bg-amber-700","bg-rose-700","bg-indigo-700","bg-sky-700","bg-violet-700","bg-emerald-700"];
    const bg = colors[(name.charCodeAt(0) || 0) % colors.length];
    return (
        <div className={`${bg} rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-lg`}
            style={{ width: size, height: size }}>
            {initials}
        </div>
    );
}

export default function LeadProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    // Nova nota/historico
    const [noteType, setNoteType] = useState("nota");
    const [noteText, setNoteText] = useState("");
    const [addingNote, setAddingNote] = useState(false);

    const load = async () => {
        try {
            const res = await leadsAPI.get(id);
            setLead(res.data);
            setHistory(res.data.history || []);
            setForm({
                name: res.data.name, email: res.data.email, phone: res.data.phone,
                segmento: res.data.segmento, status: res.data.status, origem: res.data.origem,
                notes: res.data.notes, tags: res.data.tags || [],
                opted_in_whatsapp: res.data.opted_in_whatsapp,
                opted_in_email: res.data.opted_in_email,
            });
        } catch { toast.error("Lead não encontrado"); navigate("/leads"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [id]);

    const save = async () => {
        setSaving(true);
        try {
            await leadsAPI.update(id, form);
            toast.success("Lead atualizado");
            setEditing(false);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
        finally { setSaving(false); }
    };

    const addNote = async () => {
        if (!noteText.trim()) return;
        setAddingNote(true);
        try {
            await leadsAPI.addHistory(id, {
                tipo: noteType, descricao: noteText,
                created_at: new Date().toISOString(),
            });
            setNoteText("");
            toast.success("Nota adicionada");
            load();
        } catch { toast.error("Erro ao adicionar nota"); }
        finally { setAddingNote(false); }
    };

    if (loading) return <div className="p-6 text-stone-400 text-sm">Carregando...</div>;
    if (!lead) return null;

    return (
        <div className="p-6 space-y-6 max-w-4xl">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button onClick={() => navigate("/leads")}
                    className="p-2 rounded-lg text-stone-400 hover:text-stone-100 hover:bg-stone-800 transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <Avatar name={lead.name} />
                <div className="flex-1 min-w-0">
                    <h1 className="text-xl font-bold text-stone-100">{lead.name}</h1>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${SEGMENTO_COLORS[lead.segmento] || SEGMENTO_COLORS.geral}`}>
                            {SEGMENTO_OPTIONS.find(s => s.value === lead.segmento)?.label || lead.segmento}
                        </span>
                        <span className="text-xs text-stone-500">· {lead.status}</span>
                        {lead.origem && <span className="text-xs text-stone-500">· {lead.origem}</span>}
                    </div>
                </div>
                <div className="flex gap-2">
                    {!editing ? (
                        <button onClick={() => setEditing(true)}
                            className="flex items-center gap-2 px-3 py-2 border border-stone-700 rounded-lg text-sm text-stone-300 hover:text-stone-100 hover:bg-stone-800 transition-colors">
                            <Edit className="w-3.5 h-3.5" /> Editar
                        </button>
                    ) : (
                        <>
                            <button onClick={() => setEditing(false)}
                                className="p-2 border border-stone-700 rounded-lg text-stone-400 hover:text-stone-200 transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                            <button onClick={save} disabled={saving}
                                className="flex items-center gap-2 px-3 py-2 bg-amber-700 hover:bg-amber-600 text-white rounded-lg text-sm disabled:opacity-50 transition-colors">
                                <Save className="w-3.5 h-3.5" /> {saving ? "Salvando..." : "Salvar"}
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Dados do lead */}
                <div className="md:col-span-2 space-y-4">
                    <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                        <h2 className="text-sm font-semibold text-stone-300 mb-4">Informações de contato</h2>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { label: "Nome", field: "name", type: "text" },
                                { label: "Email", field: "email", type: "email" },
                                { label: "Telefone", field: "phone", type: "tel" },
                                { label: "Origem", field: "origem", type: "text" },
                            ].map(({ label, field, type }) => (
                                <div key={field}>
                                    <label className="text-xs text-stone-500 block mb-1">{label}</label>
                                    {editing ? (
                                        <input type={type}
                                            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                            value={form[field] || ""} onChange={e => setForm(f => ({...f, [field]: e.target.value}))} />
                                    ) : (
                                        <p className="text-sm text-stone-200">{lead[field] || "—"}</p>
                                    )}
                                </div>
                            ))}
                            <div>
                                <label className="text-xs text-stone-500 block mb-1">Jornada</label>
                                {editing ? (
                                    <select className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                        value={form.segmento} onChange={e => setForm(f => ({...f, segmento: e.target.value}))}>
                                        {SEGMENTO_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                ) : (
                                    <p className="text-sm text-stone-200">{SEGMENTO_OPTIONS.find(s => s.value === lead.segmento)?.label || lead.segmento}</p>
                                )}
                            </div>
                            <div>
                                <label className="text-xs text-stone-500 block mb-1">Status</label>
                                {editing ? (
                                    <select className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                        value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
                                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                ) : (
                                    <p className="text-sm text-stone-200">{lead.status}</p>
                                )}
                            </div>
                        </div>

                        {/* Notas */}
                        <div className="mt-4">
                            <label className="text-xs text-stone-500 block mb-1">Notas</label>
                            {editing ? (
                                <textarea rows={3} className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600 resize-none"
                                    value={form.notes || ""} onChange={e => setForm(f => ({...f, notes: e.target.value}))} />
                            ) : (
                                <p className="text-sm text-stone-400">{lead.notes || "—"}</p>
                            )}
                        </div>
                    </div>

                    {/* Histórico */}
                    <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                        <h2 className="text-sm font-semibold text-stone-300 mb-4">Histórico</h2>

                        {/* Adicionar nota */}
                        <div className="bg-stone-800 rounded-xl p-3 mb-4 space-y-2">
                            <div className="flex gap-2">
                                <select className="bg-stone-700 border border-stone-600 rounded-lg px-2 py-1.5 text-xs text-stone-200 focus:outline-none"
                                    value={noteType} onChange={e => setNoteType(e.target.value)}>
                                    {HISTORY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                                <input className="flex-1 bg-stone-700 border border-stone-600 rounded-lg px-3 py-1.5 text-xs text-stone-100 placeholder:text-stone-500 focus:outline-none"
                                    placeholder="Escreva uma nota..." value={noteText} onChange={e => setNoteText(e.target.value)}
                                    onKeyDown={e => e.key === "Enter" && addNote()} />
                                <button onClick={addNote} disabled={addingNote || !noteText.trim()}
                                    className="px-3 py-1.5 bg-amber-700 hover:bg-amber-600 text-white rounded-lg text-xs disabled:opacity-50 transition-colors">
                                    <Plus className="w-3 h-3" />
                                </button>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {history.length === 0 && (
                                <p className="text-xs text-stone-500 text-center py-4">Nenhuma interação registrada</p>
                            )}
                            {history.map((h, i) => (
                                <div key={i} className="flex items-start gap-3">
                                    <div className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-1.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-stone-300">{h.tipo}</span>
                                            {h.canal && <span className="text-xs text-stone-500">via {h.canal}</span>}
                                            <span className="text-xs text-stone-600 ml-auto">{fmtDate(h.created_at)}</span>
                                        </div>
                                        <p className="text-xs text-stone-400 mt-0.5">{h.descricao}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Sidebar direita */}
                <div className="space-y-4">
                    {/* Canais opt-in */}
                    <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                        <h2 className="text-sm font-semibold text-stone-300 mb-3">Canais</h2>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <MessageCircle className="w-4 h-4 text-green-400" />
                                    <span className="text-xs text-stone-300">WhatsApp</span>
                                </div>
                                {editing ? (
                                    <input type="checkbox" checked={form.opted_in_whatsapp}
                                        onChange={e => setForm(f => ({...f, opted_in_whatsapp: e.target.checked}))} />
                                ) : (
                                    <span className={`text-xs ${lead.opted_in_whatsapp ? "text-green-400" : "text-stone-600"}`}>
                                        {lead.opted_in_whatsapp ? "Opt-in ✓" : "Sem opt-in"}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-sky-400" />
                                    <span className="text-xs text-stone-300">Email</span>
                                </div>
                                {editing ? (
                                    <input type="checkbox" checked={form.opted_in_email}
                                        onChange={e => setForm(f => ({...f, opted_in_email: e.target.checked}))} />
                                ) : (
                                    <span className={`text-xs ${lead.opted_in_email ? "text-green-400" : "text-stone-600"}`}>
                                        {lead.opted_in_email ? "Opt-in ✓" : "Sem opt-in"}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tags */}
                    <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                        <h2 className="text-sm font-semibold text-stone-300 mb-3 flex items-center gap-2">
                            <Tag className="w-4 h-4 text-amber-400" /> Tags
                        </h2>
                        <div className="flex flex-wrap gap-1.5">
                            {(editing ? form.tags : lead.tags || []).map((tag, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 bg-amber-900/30 text-amber-300 border border-amber-700/50 rounded-full flex items-center gap-1">
                                    {tag}
                                    {editing && (
                                        <button onClick={() => setForm(f => ({...f, tags: f.tags.filter((_, j) => j !== i)}))}
                                            className="hover:text-red-300">×</button>
                                    )}
                                </span>
                            ))}
                            {editing && (
                                <input className="text-xs bg-stone-800 border border-stone-700 rounded-full px-2 py-0.5 text-stone-300 focus:outline-none w-24"
                                    placeholder="+ tag"
                                    onKeyDown={e => {
                                        if (e.key === "Enter" && e.target.value.trim()) {
                                            setForm(f => ({...f, tags: [...f.tags, e.target.value.trim()]}));
                                            e.target.value = "";
                                        }
                                    }} />
                            )}
                            {!editing && lead.tags?.length === 0 && <p className="text-xs text-stone-600">Sem tags</p>}
                        </div>
                    </div>

                    {/* Datas */}
                    <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                        <h2 className="text-sm font-semibold text-stone-300 mb-3 flex items-center gap-2">
                            <Clock className="w-4 h-4 text-stone-400" /> Datas
                        </h2>
                        <div className="space-y-2">
                            <div>
                                <p className="text-xs text-stone-500">Cadastro</p>
                                <p className="text-xs text-stone-300">{fmtDate(lead.created_at)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-stone-500">Atualização</p>
                                <p className="text-xs text-stone-300">{fmtDate(lead.updated_at)}</p>
                            </div>
                            {lead.last_interaction && (
                                <div>
                                    <p className="text-xs text-stone-500">Última interação</p>
                                    <p className="text-xs text-stone-300">{fmtDate(lead.last_interaction)}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
